







































































































































































































































































































































































import { defineComponent, ref, computed, watch, reactive } from '@vue/composition-api';
import Disclaimer from '@/components/Disclaimer.vue';
import store from '@/store';

import { VideoUploadParams, VideoUploadMetadata, UserLevel } from '@/types';
import { uploadVideoFileMenu, files } from '../state';
import { useClient } from '@/use';

export default defineComponent({
  name: 'UploadVideoFileMenu',
  components: { Disclaimer },
  setup() {
    const client = useClient();
    const subjectRaces = computed(() => store.state.application.applicationData?.lists.subject_races);
    const subjectSex = computed(() => store.state.application.applicationData?.lists.subject_sex);
    const procedure = computed(() => store.state.application.applicationData?.lists.procedure);
    const observationPeriod = computed(() => store.state.application.applicationData?.lists.observation_period);
    const dataSources = computed(() => {
      // If user level is standard user, just return their allowed institutions
      if (client?.user?.user_level === UserLevel.User || client?.user?.user_level === UserLevel.Userhigh) {
        return client.user.accessible_institutions;
      }

      // Otherwise return all possible
      return store.state.application.applicationData?.lists.institutions;
    });

    const formsValidation = ref([]);
    const sharedFormValid = ref(false);
    const allFormsValid = computed(() => formsValidation.value.every((val) => val));
    const maximum100Rule = (val: string) => (val && val.length <= 100) || 'Maximum 100 characters required';
    const min0Max6Rule = (val: number) => !val || (val >= 0 && val <= 6) || 'the number is out of range';
    const min0Rule = (val: number) => !val || (val >= 0) || 'the number is out of range';

    const defaultSubjectSex = computed(() => (subjectSex.value && subjectSex.value[subjectSex.value.length - 1]) || '');
    const defaultMeta = () => (reactive({
      subject_race: '',
      subject_sex: defaultSubjectSex.value,
      procedure: '',
      observation_period: '',
      data_source: '',
      details: [''],
      remarks: '',
    }));

    const step = ref(0);
    const meta = ref<VideoUploadMetadata[]>([]);
    const fileNames = ref<string[]>([]);
    const shareMeta = ref(true);
    const sharedMeta = ref<VideoUploadMetadata>(defaultMeta());

    // Navigation guards
    const showNextButton = computed(() => (
      !shareMeta.value
      && files.value.length > 1
      && step.value !== files.value.length - 1
    ));
    const uploadDisabled = computed(() => {
      if (shareMeta.value && sharedFormValid.value) {
        return false;
      }

      if (!shareMeta.value && allFormsValid.value) {
        return false;
      }

      return true;
    });

    // If files change, reset metadata
    watch(files, (val) => {
      sharedMeta.value = defaultMeta();
      meta.value = val.map(() => defaultMeta());
      fileNames.value = val.map((file) => file.name);
    });

    function truncateFileName(filename: string): string {
      const maxLength = 15;
      const [main, ...exts] = filename.split('.');
      const ext = exts[exts.length - 1];

      if (filename.length > maxLength) {
        return `${main.substring(0, maxLength)}...${ext}`;
      }

      return filename;
    }

    const emptyMessage = 'Field cannot be empty.';
    const nonEmptyRule = (val: string) => (!!val || emptyMessage);
    const nonEmptyArrayRule = (val: unknown[]) => (!!val.length || emptyMessage);

    const acceptedVideoTypes = 'video/mp4';
    const videoTypesRule = (inputFiles: File[]) => (
      inputFiles.every((file) => acceptedVideoTypes.split(',').includes(file.type)) || 'Only MP4 files are supported.'
    );

    function closeDialog() {
      uploadVideoFileMenu.value = false;
      files.value = [];
    }

    function uploadVideos() {
      if (!files.value) {
        return;
      }

      // Combine params
      const videos: VideoUploadParams[] = files.value.map((file, i) => {
        const data = {
          file,
          meta: shareMeta.value ? sharedMeta.value : meta.value[i],
        };
        data.meta.gestation_age = Number(data.meta.gestation_age_week) * 7 + Number(data.meta.gestation_age_day);
        data.meta.age = Number(data.meta.age);
        delete data.meta.gestation_age_week;
        delete data.meta.gestation_age_day;

        // Rename if necessary
        if (fileNames.value[i] !== file.name) {
          data.file = new File([data.file], fileNames.value[i], {
            type: data.file.type,
            lastModified: data.file.lastModified,
          });
        }

        return data;
      });

      store.dispatch.upload.uploadVideos(videos);
      store.commit.upload.setDialogOpen(true);
      closeDialog();
    }

    function handleClickRemove(counter: number) {
      sharedMeta.value.details?.splice(counter, 1);
    }

    function handleClickAdd() {
      sharedMeta.value.details?.push('');
    }

    return {
      subjectRaces,
      subjectSex,
      procedure,
      observationPeriod,
      dataSources,

      formsValidation,
      allFormsValid,
      maximum100Rule,
      min0Max6Rule,
      min0Rule,
      step,
      files,
      meta,
      fileNames,
      shareMeta,
      sharedMeta,
      sharedFormValid,
      showNextButton,
      uploadDisabled,

      truncateFileName,
      closeDialog,
      uploadVideos,
      handleClickRemove,
      handleClickAdd,
      nonEmptyRule,
      nonEmptyArrayRule,
      acceptedVideoTypes,
      videoTypesRule,
    };
  },
});
